import React, { useEffect, useState } from 'react'
import {
  Autocomplete,
  Box,
  Typography,
  Button,
  IconButton,
  Input,
  Select,
  Option,
  Checkbox,
  Card,
  CardContent,
  Stack,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Textarea,
  Link
} from '@mui/joy'
import {
  Plus as Add,
  Trash as Delete,
  DotsSixVertical as DragIndicator,
  CaretUp as KeyboardArrowUp,
  TextB as FormatBold
} from '@phosphor-icons/react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { BackendSubLineItem } from '../../types/billing-backend'
import type { EditableLineItem, TaxRate } from '../../types/billing'
import { materialSpecsApi, type MaterialSpecification } from '../../lib/distributorsApi'

interface LineItemsEditorProps {
  lineItems: EditableLineItem[]
  onLineItemsChange: (items: EditableLineItem[]) => void
  /** Org tax rates; a line with none of its own is taxed at the default. */
  taxRates?: TaxRate[]
  disabled?: boolean
}

interface SortableLineItemProps {
  item: EditableLineItem
  index: number
  onUpdate: (index: number, updates: Partial<EditableLineItem>) => void
  onRemove: (index: number) => void
  onAddSubItem: (parentIndex: number) => void
  onUpdateSubItem: (parentIndex: number, subIndex: number, updates: Partial<BackendSubLineItem>) => void
  onRemoveSubItem: (parentIndex: number, subIndex: number) => void
  taxRates: TaxRate[]
  disabled: boolean
  showNotes: boolean
  onToggleNotes: () => void
}

/**
 * Type-ahead over the material catalog, so a line item can be picked instead of
 * re-typed. The search runs server-side (the catalog is far larger than one
 * page) and only once the term is worth narrowing on.
 */
function useMaterialSpecSearch() {
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<MaterialSpecification[]>([])

  useEffect(() => {
    const term = query.trim()
    if (term.length < 2) {
      setOptions([])
      return
    }
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const page = await materialSpecsApi.list({ search: term, is_active: true })
        if (!cancelled) setOptions(page.results || [])
      } catch {
        // A catalog that is unreachable must not block hand-typed lines.
        if (!cancelled) setOptions([])
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query])

  return { options, setQuery }
}

interface SubLineItemRowProps {
  item: BackendSubLineItem
  parentIndex: number
  index: number
  disabled: boolean
  onUpdate: (parentIndex: number, subIndex: number, updates: Partial<BackendSubLineItem>) => void
  onRemove: (parentIndex: number, subIndex: number) => void
}

function SubLineItemRow({ item, parentIndex, index, disabled, onUpdate, onRemove }: SubLineItemRowProps) {
  const total = (typeof item.total === 'string' ? parseFloat(item.total) : item.total) || 0
  return (
    <Card variant="soft" sx={{ p: 1, ml: { xs: 0, lg: 4 }, bgcolor: 'background.level1' }}>
      <Grid container spacing={1} alignItems="center">
        <Grid xs={12} lg={5}>
          <FormControl disabled={disabled}>
            <FormLabel sx={{ display: { xs: 'block', lg: 'none' } }}>Description</FormLabel>
            <Input
              placeholder="Internal item description"
              value={item.description}
              onChange={(e) => onUpdate(parentIndex, index, { description: e.target.value })}
              size="sm"
            />
          </FormControl>
        </Grid>
        <Grid xs={6} lg={2}>
          <FormControl disabled={disabled}>
            <FormLabel sx={{ display: { xs: 'block', lg: 'none' } }}>Type</FormLabel>
            <Select
              value={item.item_type}
              onChange={(_, value) => value && onUpdate(parentIndex, index, { item_type: value })}
              size="sm"
            >
              <Option value="material">Material</Option>
              <Option value="labor">Labor</Option>
              <Option value="equipment">Equipment</Option>
              <Option value="service">Service</Option>
              <Option value="other">Other</Option>
            </Select>
          </FormControl>
        </Grid>
        <Grid xs={4} lg={1.5}>
          <FormControl disabled={disabled}>
            <FormLabel sx={{ display: { xs: 'block', lg: 'none' } }}>Qty</FormLabel>
            <Input
              type="number"
              value={item.quantity}
              onChange={(e) => onUpdate(parentIndex, index, { quantity: parseFloat(e.target.value) || 0 })}
              slotProps={{ input: { min: 0, step: 0.01 } }}
              size="sm"
            />
          </FormControl>
        </Grid>
        <Grid xs={4} lg={1.5}>
          <FormControl disabled={disabled}>
            <FormLabel sx={{ display: { xs: 'block', lg: 'none' } }}>Unit Price</FormLabel>
            <Input
              type="number"
              value={item.unit_price}
              onChange={(e) => onUpdate(parentIndex, index, { unit_price: parseFloat(e.target.value) || 0 })}
              startDecorator="$"
              slotProps={{ input: { min: 0, step: 0.01 } }}
              size="sm"
            />
          </FormControl>
        </Grid>
        <Grid xs={3} lg={1.5}>
          <FormControl>
            <FormLabel sx={{ display: { xs: 'block', lg: 'none' } }}>Total</FormLabel>
            <Typography level="body-sm" textColor="text.secondary">${total.toFixed(2)}</Typography>
          </FormControl>
        </Grid>
        <Grid xs={1} lg={0.5} sx={{ textAlign: 'right' }}>
          <IconButton
            color="danger"
            size="sm"
            variant="plain"
            onClick={() => onRemove(parentIndex, index)}
            disabled={disabled}
            aria-label="Remove sub-item"
          >
            <Delete size={18} />
          </IconButton>
        </Grid>
      </Grid>
    </Card>
  )
}

function SortableLineItem({
  item,
  index,
  onUpdate,
  onRemove,
  onAddSubItem,
  onUpdateSubItem,
  onRemoveSubItem,
  taxRates,
  disabled,
  showNotes,
  onToggleNotes
}: SortableLineItemProps) {
  const { options: materialOptions, setQuery: setMaterialQuery } = useMaterialSpecSearch()
  const [catalogOpen, setCatalogOpen] = useState(false)

  const applyMaterialSpec = (spec: MaterialSpecification) => {
    const currentPrice = typeof item.unit_price === 'string' ? parseFloat(item.unit_price) : item.unit_price
    onUpdate(index, {
      description: spec.name,
      item_type: 'material',
      // The spec id is what later turns this line into a material order.
      material_spec_id: spec.id,
      // A price the user already typed is theirs; only an untouched row takes
      // the catalog cost.
      unit_price: !currentPrice && spec.default_unit_cost ? parseFloat(spec.default_unit_cost) : item.unit_price
    })
  }

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ 
    id: item.id || `new-${index}`,
    disabled
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const isBold = item.is_bold || false

  return (
    <Card 
      ref={setNodeRef}
      style={style}
      {...attributes}
      variant="outlined"
      sx={{
        opacity: isDragging ? 0.5 : 1,
        backgroundColor: isDragging ? 'background.level1' : 'background.surface'
      }}
    >
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          {/* Drag Handle */}
          <Grid xs={12} lg={0.5}>
            <IconButton
              size="sm"
              disabled={disabled}
              sx={{ 
                cursor: disabled ? 'default' : 'grab',
                '&:active': { cursor: 'grabbing' }
              }}
              {...listeners}
            >
              <DragIndicator />
            </IconButton>
          </Grid>

          {/* Main Description (catalog type-ahead, with inline Bold toggle) */}
          <Grid xs={12} lg={3.5}>
            <FormControl disabled={disabled}>
              <FormLabel sx={{ display: { xs: 'block', lg: 'none' } }}>
                Description
              </FormLabel>
              <Autocomplete
                freeSolo
                disableClearable
                size="sm"
                placeholder="Item description or catalog search"
                disabled={disabled}
                options={materialOptions}
                // The search already ran server-side; filtering again here
                // would hide matches on fields we don't display.
                filterOptions={(x) => x}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                inputValue={item.description}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') {
                    onUpdate(index, { description: value })
                    setMaterialQuery(value)
                  }
                }}
                onChange={(_, value) => {
                  if (value && typeof value !== 'string') {
                    applyMaterialSpec(value)
                  }
                }}
                // Suggestions only when the catalog actually has some; typing a
                // line the catalog has never heard of must stay frictionless.
                open={catalogOpen && materialOptions.length > 0}
                onOpen={() => setCatalogOpen(true)}
                onClose={() => setCatalogOpen(false)}
                startDecorator={
                  <IconButton
                    size="sm"
                    variant={isBold ? 'solid' : 'plain'}
                    color={isBold ? 'primary' : 'neutral'}
                    onClick={() => onUpdate(index, { is_bold: !isBold })}
                    disabled={disabled}
                    aria-label={isBold ? 'Remove bold formatting' : 'Bold this line'}
                    sx={{ '--IconButton-size': '24px', minHeight: 24, minWidth: 24 }}
                  >
                    <FormatBold size={18} />
                  </IconButton>
                }
                sx={{
                  fontWeight: isBold ? 'bold' : 'normal'
                }}
              />
            </FormControl>
          </Grid>

          {/* Type */}
          <Grid xs={12} lg={1.5}>
            <FormControl disabled={disabled} size="sm">
              <FormLabel sx={{ display: { xs: 'block', lg: 'none' } }}>
                Type
              </FormLabel>
              <Select
                value={item.item_type}
                onChange={(_, value) => value && onUpdate(index, { item_type: value })}
                size="sm"
                sx={{
                  fontWeight: isBold ? 'bold' : 'normal'
                }}
              >
                <Option value="service">Service</Option>
                <Option value="material">Material</Option>
                <Option value="labor">Labor</Option>
                <Option value="other">Other</Option>
              </Select>
            </FormControl>
          </Grid>

          {/* Quantity */}
          <Grid xs={6} lg={1}>
            <FormControl disabled={disabled}>
              <FormLabel sx={{ display: { xs: 'block', lg: 'none' } }}>
                Quantity
              </FormLabel>
              <Input
                type="number"
                value={item.quantity}
                onChange={(e) => onUpdate(index, { quantity: parseFloat(e.target.value) || 0 })}
                slotProps={{
                  input: {
                    min: 0,
                    step: 1
                  }
                }}
                size="sm"
                sx={{
                  fontWeight: isBold ? 'bold' : 'normal'
                }}
              />
            </FormControl>
          </Grid>

          {/* Unit Price */}
          <Grid xs={6} lg={1.5}>
            <FormControl disabled={disabled}>
              <FormLabel sx={{ display: { xs: 'block', lg: 'none' } }}>
                Unit Price
              </FormLabel>
              <Input
                type="number"
                value={item.unit_price}
                onChange={(e) => onUpdate(index, { unit_price: parseFloat(e.target.value) || 0 })}
                startDecorator="$"
                slotProps={{
                  input: {
                    min: 0,
                    step: 0.01
                  }
                }}
                size="sm"
                sx={{
                  fontWeight: isBold ? 'bold' : 'normal'
                }}
              />
            </FormControl>
          </Grid>

          {/* Taxable (checkbox + inline %, then which rate) */}
          <Grid xs={12} lg={2.5}>
            <FormControl disabled={disabled} size="sm">
              <FormLabel sx={{ display: { xs: 'block', lg: 'none' } }}>
                Taxable
              </FormLabel>
              <Stack spacing={0.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={item.is_taxable}
                    onChange={(e) => onUpdate(index, {
                      is_taxable: e.target.checked,
                      tax_rate: null,
                      taxable_pct: e.target.checked ? (item.taxable_pct || 1) : 1
                    })}
                    disabled={disabled}
                    size="sm"
                  />
                  {item.is_taxable ? (
                    <Input
                      type="number"
                      value={Math.round(((typeof item.taxable_pct === 'string' ? parseFloat(item.taxable_pct) : item.taxable_pct) || 1) * 100)}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0
                        onUpdate(index, {
                          taxable_pct: Math.min(100, Math.max(0, value)) / 100
                        })
                      }}
                      endDecorator="%"
                      slotProps={{
                        input: {
                          min: 0,
                          max: 100,
                          step: 1
                        }
                      }}
                      size="sm"
                      disabled={disabled}
                      sx={{
                        fontWeight: isBold ? 'bold' : 'normal',
                        flex: 1,
                        minWidth: 0
                      }}
                    />
                  ) : (
                    <Typography
                      level="body-sm"
                      sx={{ color: 'text.tertiary', flex: 1 }}
                    >
                      —
                    </Typography>
                  )}
                </Stack>
                {/* Which rate. Left on the default, the line is taxed at the
                    org's active rate — the same one the server applies. */}
                {item.is_taxable && taxRates.length > 0 && (
                  <Select
                    value={item.tax_rate ? String(item.tax_rate) : ''}
                    onChange={(_, value) => onUpdate(index, { tax_rate: value ? parseInt(value) : null })}
                    size="sm"
                    disabled={disabled}
                  >
                    <Option value="">Default rate</Option>
                    {taxRates
                      .filter(rate => rate.is_active)
                      .map(rate => (
                        <Option key={rate.id} value={String(rate.id)}>
                          {rate.name} ({rate.rate_percentage}%)
                        </Option>
                      ))}
                  </Select>
                )}
              </Stack>
            </FormControl>
          </Grid>

          {/* Total */}
          <Grid xs={6} lg={1}>
            <FormControl>
              <FormLabel sx={{ display: { xs: 'block', lg: 'none' } }}>
                Total
              </FormLabel>
              <Typography
                level="body-md"
                fontWeight={isBold ? 'bold' : 'lg'}
                sx={{ whiteSpace: 'nowrap' }}
              >
                ${(typeof item.total === 'string' ? parseFloat(item.total) : item.total).toFixed(2)}
              </Typography>
            </FormControl>
          </Grid>

          {/* Delete Button */}
          <Grid xs={6} lg={0.5} sx={{ textAlign: 'right' }}>
            <IconButton
              color="danger"
              size="sm"
              onClick={() => onRemove(index)}
              disabled={disabled}
            >
              <Delete />
            </IconButton>
          </Grid>

          {/* Internal Sub-Line-Items (org-only, never shown to customer) */}
          <Grid xs={12}>
            <Box sx={{ mt: 1, pl: { xs: 0, lg: 2 } }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Typography
                  level="body-xs"
                  textColor="text.tertiary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 'lg' }}
                >
                  Internal sub-items
                </Typography>
                <Typography level="body-xs" textColor="text.tertiary">
                  — not visible to customer
                </Typography>
              </Stack>
              {(item.sub_items && item.sub_items.length > 0) && (
                <Stack spacing={1} sx={{ mb: 1 }}>
                  {item.sub_items.map((subItem, subIndex) => (
                    <SubLineItemRow
                      key={subItem.id ?? `new-sub-${index}-${subIndex}`}
                      item={subItem}
                      parentIndex={index}
                      index={subIndex}
                      disabled={disabled}
                      onUpdate={onUpdateSubItem}
                      onRemove={onRemoveSubItem}
                    />
                  ))}
                </Stack>
              )}
              <Link
                component="button"
                level="body-sm"
                onClick={() => onAddSubItem(index)}
                disabled={disabled}
                sx={{ textDecoration: 'none' }}
              >
                + Add internal sub-item
              </Link>
            </Box>
          </Grid>

          {/* Add Notes Section */}
          <Grid xs={12}>
            <Box sx={{ mt: 1 }}>
              {!showNotes && !item.notes ? (
                <Link
                  component="button"
                  level="body-sm"
                  onClick={onToggleNotes}
                  sx={{ textDecoration: 'none' }}
                  disabled={disabled}
                >
                  + Add Notes
                </Link>
              ) : (
                <>
                  {!item.notes && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography level="body-sm">Notes</Typography>
                      <IconButton 
                        size="sm" 
                        onClick={onToggleNotes}
                        disabled={disabled}
                      >
                        <KeyboardArrowUp />
                      </IconButton>
                    </Box>
                  )}
                  {(showNotes || item.notes) && (
                    <Textarea
                      placeholder="Add detailed notes or additional information for this line item..."
                      value={item.notes || ''}
                      onChange={(e) => onUpdate(index, { notes: e.target.value })}
                      disabled={disabled}
                      minRows={2}
                      size="sm"
                      sx={{
                        fontWeight: isBold ? 'bold' : 'normal'
                      }}
                    />
                  )}
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default function LineItemsEditor({
  lineItems,
  onLineItemsChange,
  taxRates = [],
  disabled = false
}: LineItemsEditorProps) {
  const [showNotesSet, setShowNotesSet] = useState<Set<number>>(new Set())
  const [activeId, setActiveId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const addLineItem = () => {
    const newItem: EditableLineItem = {
      description: '',
      notes: '',
      quantity: 1,
      unit_price: 0,
      item_type: 'service',
      is_taxable: true,
      taxable_pct: 1,
      tax_rate: null,
      total: 0,
      sort_order: lineItems.length,
      is_bold: false,
      sub_items: []
    }
    onLineItemsChange([...lineItems, newItem])
  }

  const addSubItem = (parentIndex: number) => {
    const updated = [...lineItems]
    const parent = { ...updated[parentIndex] }
    const existing = parent.sub_items ?? []
    const newSub: BackendSubLineItem = {
      description: '',
      quantity: 1,
      unit_price: 0,
      item_type: 'material',
      total: 0,
      sort_order: existing.length,
    }
    parent.sub_items = [...existing, newSub]
    updated[parentIndex] = parent
    onLineItemsChange(updated)
  }

  const updateSubItem = (
    parentIndex: number,
    subIndex: number,
    updates: Partial<BackendSubLineItem>
  ) => {
    const updated = [...lineItems]
    const parent = { ...updated[parentIndex] }
    const subs = [...(parent.sub_items ?? [])]
    const next = { ...subs[subIndex], ...updates }
    const qty = typeof next.quantity === 'string' ? parseFloat(next.quantity) : next.quantity
    const price = typeof next.unit_price === 'string' ? parseFloat(next.unit_price) : next.unit_price
    next.total = (qty || 0) * (price || 0)
    subs[subIndex] = next
    parent.sub_items = subs
    updated[parentIndex] = parent
    onLineItemsChange(updated)
  }

  const removeSubItem = (parentIndex: number, subIndex: number) => {
    const updated = [...lineItems]
    const parent = { ...updated[parentIndex] }
    const subs = (parent.sub_items ?? []).filter((_, i) => i !== subIndex)
    subs.forEach((s, i) => { s.sort_order = i })
    parent.sub_items = subs
    updated[parentIndex] = parent
    onLineItemsChange(updated)
  }

  const updateLineItem = (index: number, updates: Partial<EditableLineItem>) => {
    const updatedItems = [...lineItems]
    const item = { ...updatedItems[index], ...updates }
    
    // Calculate total - ensure numeric values
    const quantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) : item.quantity
    const unitPrice = typeof item.unit_price === 'string' ? parseFloat(item.unit_price) : item.unit_price
    item.total = quantity * unitPrice
    
    updatedItems[index] = item
    onLineItemsChange(updatedItems)
  }

  const removeLineItem = (index: number) => {
    const updatedItems = lineItems.filter((_, i) => i !== index)
    // Update sort_order
    updatedItems.forEach((item, i) => {
      item.sort_order = i
    })
    onLineItemsChange(updatedItems)
    
    // Remove from showNotesSet
    const newShowNotesSet = new Set(showNotesSet)
    newShowNotesSet.delete(index)
    setShowNotesSet(newShowNotesSet)
  }

  const toggleNotes = (index: number) => {
    const newSet = new Set(showNotesSet)
    if (newSet.has(index)) {
      newSet.delete(index)
    } else {
      newSet.add(index)
    }
    setShowNotesSet(newSet)
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      const oldIndex = lineItems.findIndex(item => 
        (item.id || `new-${lineItems.indexOf(item)}`) === active.id
      )
      const newIndex = lineItems.findIndex(item => 
        (item.id || `new-${lineItems.indexOf(item)}`) === over?.id
      )

      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(lineItems, oldIndex, newIndex)
        // Update sort_order for all items
        newItems.forEach((item, i) => {
          item.sort_order = i
        })
        onLineItemsChange(newItems)
      }
    }

    setActiveId(null)
  }

  const sortableIds = lineItems.map((item, index) => item.id || `new-${index}`)

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography level="title-lg">Line Items</Typography>
        <Button
          startDecorator={<Add />}
          onClick={addLineItem}
          disabled={disabled}
          size="sm"
        >
          Add Item
        </Button>
      </Box>

      {lineItems.length === 0 ? (
        <Card variant="soft">
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography level="body-sm" sx={{ mb: 2 }}>
              No line items yet
            </Typography>
            <Button
              startDecorator={<Add />}
              onClick={addLineItem}
              disabled={disabled}
              size="sm"
            >
              Add First Item
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {/* Header */}
          <Grid container spacing={2} sx={{ display: { xs: 'none', lg: 'flex' }, px: 1 }}>
            <Grid xs={0.5}></Grid>
            <Grid xs={3.5}>
              <Typography level="body-sm" fontWeight="lg">Description</Typography>
            </Grid>
            <Grid xs={1.5}>
              <Typography level="body-sm" fontWeight="lg">Type</Typography>
            </Grid>
            <Grid xs={1}>
              <Typography level="body-sm" fontWeight="lg">Qty</Typography>
            </Grid>
            <Grid xs={1.5}>
              <Typography level="body-sm" fontWeight="lg">Unit Price</Typography>
            </Grid>
            <Grid xs={2.5}>
              <Typography level="body-sm" fontWeight="lg">Taxable</Typography>
            </Grid>
            <Grid xs={1}>
              <Typography level="body-sm" fontWeight="lg">Total</Typography>
            </Grid>
            <Grid xs={0.5}></Grid>
          </Grid>

          <Divider />

          {/* Line Items with Drag and Drop */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableIds}
              strategy={verticalListSortingStrategy}
            >
              {lineItems.map((item, index) => (
                <SortableLineItem
                  key={item.id || `new-${index}`}
                  item={item}
                  index={index}
                  onUpdate={updateLineItem}
                  onRemove={removeLineItem}
                  onAddSubItem={addSubItem}
                  onUpdateSubItem={updateSubItem}
                  onRemoveSubItem={removeSubItem}
                  taxRates={taxRates}
                  disabled={disabled}
                  showNotes={showNotesSet.has(index)}
                  onToggleNotes={() => toggleNotes(index)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Stack>
      )}
    </Box>
  )
}